package pl.finn.forms.utils.model

import io.kvision.form.check.CheckBox
import io.kvision.html.ButtonStyle
import io.kvision.html.Div
import io.kvision.html.button
import pl.finn.forms.tw1.*
import pl.finn.forms.utils.Components
import pl.finn.forms.utils.FormReadingUtils
import pl.finn.forms.utils.form.FormBuilder
import pl.finn.forms.utils.form.FormElement

class Person(reference: FormBuilder<DocumentTreeNode>) {

  private val formReference = reference
  private val components = Components(formReference)
  val address = Addresses(formReference)

  fun createNaturalPerson(osobaFizyczna: OsobaFizyczna, typeData: String, legend: String): ArrayList<Any> {
    val naturalPerson = ArrayList<Any>()
    naturalPerson.add(
      components.createFieldset(
        legend
      )
    )
    val naturalPersonData = ArrayList<Any>()
    naturalPersonData.add(
      components.createInput(
        osobaFizyczna.imie,
        "Imię"
      )
    )
    naturalPersonData.add(
      components.createInput(
        osobaFizyczna.nazwisko,
        "Nazwisko"
      )
    )
    val identityMap = HashMap<String, Any?>()
    identityMap["0"] = components.createInput(
      osobaFizyczna.nip1,
      "NIP",
      requirements = FormReadingUtils.checkValidation("TNrNIP")
    )
    identityMap["1"] = components.createInput(
      osobaFizyczna.pesel2,
      "PESEL",
      requirements = FormReadingUtils.checkValidation("TNrPESEL")
    )
    identityMap["2"] = components.createCheckbox(
      osobaFizyczna.oswiadczenieWierzyciela,
      Tw1Data.statement,
      true
    )
    naturalPersonData.add(
      components.createRadioGroup(
        osobaFizyczna.admId,
        "Identyfikator administracyjny",
        Tw1Data.optionsOfOIdentity,
        identityMap
      )
    )
    naturalPersonData.add(identityMap)

    if (typeData == "Normal" || typeData == "Long") {
      naturalPersonData.addAll(
        address.createAddress(
          osobaFizyczna.adres,
          osobaFizyczna.adresData
        )
      )
      naturalPersonData.add(
        components.createInput(
          osobaFizyczna.nrTelefonu,
          "Numer telefonu",
          isRequired = false,
          requirements = FormReadingUtils.checkValidation("TNrTelefonu")
        )
      )
      naturalPersonData.add(
        components.createInput(
          osobaFizyczna.email,
          "Adres e-mail",
          isRequired = false,
          requirements = FormReadingUtils.checkValidation("TAdresEmail")
        )
      )
    }
    if (typeData == "Long") {
      naturalPersonData.add(
        components.createDateTime(
          osobaFizyczna.dataUrodzenia,
          "Data urodzenia zobowiązanego",
          tooltipMessage = "Po zaznaczeniu check-box „<b>Oświadczenie wierzyciela</b>” pole „<b>Data urodzenia zobowiązanego</b>” jest polem obowiązkowym do wypełnienia.",
          isRequired = false
        )
      )
      naturalPersonData.add(
        components.createSelect(
          osobaFizyczna.odpowiedzialnosc,
          "Rodzaj odpowiedzialności zobowiązanego w stosunku do wskazanego podmiotu, u którego wystąpiło zobowiązanie",
          Tw1Data.listOfResponsibilityTypes
        )
      )
    }
    naturalPerson.add(naturalPersonData)
    return naturalPerson
  }

  fun createNonPhysicalPerson(osobaNiefizyczna: OsobaNiefizyczna, typeData: String): ArrayList<Any> {
    val nonPhysicalPerson = ArrayList<Any>()
    nonPhysicalPerson.add(
      components.createFieldset(
        "Osoba niefizyczna"
      )
    )
    val nonPhysicalPersonData = ArrayList<Any>()
    nonPhysicalPersonData.add(
      components.createCheckbox(
        osobaNiefizyczna.wSpadku,
        Tw1Data.inDecline,
        isRequired = false
      )
    )
    nonPhysicalPersonData.add(
      components.createInput(
        osobaNiefizyczna.nazwa,
        "Nazwa podmiotu"
      )
    )
    val identityNonPhysicalMap = HashMap<String, Any?>()
    identityNonPhysicalMap["0"] = components.createInput(
      osobaNiefizyczna.regon1,
      "REGON",
      requirements = FormReadingUtils.checkValidation("TNrREGON")
    )
    identityNonPhysicalMap["1"] = components.createInput(
      osobaNiefizyczna.nip2,
      "NIP",
      requirements = FormReadingUtils.checkValidation("TNrNIP")
    )
    identityNonPhysicalMap["2"] = components.createCheckbox(
      osobaNiefizyczna.oswiadczenie,
      Tw1Data.statement,
      true
    )
    nonPhysicalPersonData.add(
      components.createRadioGroup(
        osobaNiefizyczna.admId,
        "Identyfikator administracyjny",
        Tw1Data.optionsOfOIdentityNonPhysical,
        identityNonPhysicalMap
      )
    )
    nonPhysicalPersonData.add(identityNonPhysicalMap)
    if (typeData == "Medium" || typeData == "Long") {
      nonPhysicalPersonData.addAll(
        address.createAddress(
          osobaNiefizyczna.adres,
          osobaNiefizyczna.adresData
        )
      )
      nonPhysicalPersonData.add(
        components.createInput(
          osobaNiefizyczna.nrTelefonu,
          "Numer telefonu",
          isRequired = false,
          requirements = FormReadingUtils.checkValidation("TNrTelefonu")
        )
      )
      nonPhysicalPersonData.add(
        components.createInput(
          osobaNiefizyczna.email,
          "Adres e-mail",
          isRequired = false,
          requirements = FormReadingUtils.checkValidation("TAdresEmail")
        )
      )
    }
    if (typeData == "Long") {
      nonPhysicalPersonData.add(
        components.createSelect(
          osobaNiefizyczna.odpowiedzialnosc,
          "Rodzaj odpowiedzialności zobowiązanego w stosunku do wskazanego podmiotu, u którego wystąpiło zobowiązanie",
          Tw1Data.listOfResponsibilityTypes
        )
      )
    }
    nonPhysicalPerson.add(nonPhysicalPersonData)
    return nonPhysicalPerson
  }

  fun createdObligatedPerson(
    documentTreeNode: DocumentTreeNode,
    zobowiazanyPierwotny: ZobowiazanyPierwotny
  ): ArrayList<Any> {
    val finnId = "collapseFinn_" + Util.getRandomString()
    val obligated = ArrayList<Any>()
    val obligatedPerson = ArrayList<Any>()
    val primaryObligatedMap = HashMap<String, Any?>()
    val naturalPersonPrimary =
      createNaturalPerson(
        zobowiazanyPierwotny.osobaFizyczna,
        "Short", "Osoba fizyczna"
      )
    val nonPhysicalPersonPrimary = createNonPhysicalPerson(
      zobowiazanyPierwotny.osobaNiefizyczna,
      "Short"
    )
    primaryObligatedMap["0"] = naturalPersonPrimary
    primaryObligatedMap["1"] = nonPhysicalPersonPrimary
    val obligatedRadio = components.createRadioGroup(
      zobowiazanyPierwotny.obligated,
      "Zobowiązany",
      Tw1Data.optionsOfPrimalObligated,
      primaryObligatedMap
    )
    obligatedPerson.add(obligatedRadio)
    obligatedPerson.add(primaryObligatedMap)

    val collapsedNPDiv = Div(className = "collapse") { id = finnId }

    val collapseObligated = components.createCollapseCheckbox(
      documentTreeNode,
      "Dane zobowiązanego, u którego powstał obowiązek",
      finnId,
      obligatedPerson,
      collapsedNPDiv
    )
    obligated.add(collapseObligated)
    components.changeElementChildrenStatus(
      obligatedPerson,
      (collapseObligated.field as CheckBox).value
    )
    obligated.add(collapsedNPDiv)
    obligated.add(obligatedPerson)
    return obligated
  }

  fun createdObligatedPersons(documentTreeNode: DocumentTreeNode, zobowiazaniPierwotni: ArrayList<ZobowiazanyPierwotny>): ArrayList<Any> {
    val finnId = "collapseFinn_" + Util.getRandomString()
    val obligated = ArrayList<Any>()
    val collapsedNPDiv = Div(className = "collapse") { id = finnId }
    val obligatedPersons = createObligatedPersonsList(zobowiazaniPierwotni)
    val collapseObligated = components.createCollapseCheckbox(
      documentTreeNode,
      "Dane zobowiązanych, u których powstał obowiązek",
      finnId,
      obligatedPersons,
      collapsedNPDiv
    )
    obligated.add(collapseObligated)
    components.changeElementChildrenStatus(
      obligatedPersons,
      (collapseObligated.field as CheckBox).value
    )
    obligated.add(collapsedNPDiv)
    obligated.add(obligatedPersons)
    return obligated
  }

  private fun createObligatedPersonsList(zobowiazaniPierwotni: ArrayList<ZobowiazanyPierwotny>): ArrayList<Any> {
    val finnId = "collapseFinn_" + Util.getRandomString()
    val div = Div() { id = finnId }
    val ObligatedPersonsDiv = ArrayList<Any>()
    val ObligatedPersonsList = ArrayList<Any>()

    if(zobowiazaniPierwotni.isEmpty()){//todo czy potrzebne
      zobowiazaniPierwotni.add(ZobowiazanyPierwotny())
    }

    zobowiazaniPierwotni.forEach {
      ObligatedPersonsList.add(
        createObligatedPersonsElement(it)
      )
    }
    
    ObligatedPersonsDiv.add(div)
    ObligatedPersonsDiv.add(arrayListOf(
      ObligatedPersonsList,
      createObligatedPersonsButtons(zobowiazaniPierwotni, div)
    ))
    return ObligatedPersonsDiv
  }

  private fun createObligatedPersonsElement(zobowiazanyPierwotny: ZobowiazanyPierwotny): ArrayList<Any> {
    val obligatedPerson = ArrayList<Any>()
    val primaryObligatedMap = HashMap<String, Any?>()
    val naturalPersonPrimary =
      createNaturalPerson(
        zobowiazanyPierwotny.osobaFizyczna,
        "Short", "Osoba fizyczna"
      )
    val nonPhysicalPersonPrimary = createNonPhysicalPerson(
      zobowiazanyPierwotny.osobaNiefizyczna,
      "Short"
    )
    primaryObligatedMap["0"] = naturalPersonPrimary
    primaryObligatedMap["1"] = nonPhysicalPersonPrimary
    val obligatedRadio = components.createRadioGroup(
      zobowiazanyPierwotny.obligated,
      "Zobowiązany",
      Tw1Data.optionsOfPrimalObligated,
      primaryObligatedMap
    )
    obligatedPerson.add(obligatedRadio)
    obligatedPerson.add(primaryObligatedMap)

    return obligatedPerson
  }

  private fun createObligatedPersonsButtons(zobowiazaniPierwotni: ArrayList<ZobowiazanyPierwotny>, div: Div): Div {
    return (
      Div(className = "p-2 d-flow-root") {
        if (zobowiazaniPierwotni.size > 1) {
          button(
            "Usuń",
            "far fa-minus-square",
            ButtonStyle.OUTLINEPRIMARY, className = "btn-sm"
          ).onClick {

            zobowiazaniPierwotni.removeAt(zobowiazaniPierwotni.lastIndex)
            val newBudgetImplementationsList = ArrayList<Any>()
            zobowiazaniPierwotni.forEach {
              newBudgetImplementationsList.add(
                createObligatedPersonsElement(it)
              )
            }
            div.removeAll()
            components.readComponentsList(newBudgetImplementationsList, div)
            components.readComponentsList(createObligatedPersonsButtons(zobowiazaniPierwotni, div), div)
          }

        }
        button(
          "Dodaj",
          "far fa-plus-square",
          ButtonStyle.OUTLINEPRIMARY, className = "btn-sm float-right"
        ).onClick {

          zobowiazaniPierwotni.add(ZobowiazanyPierwotny())
          val newBudgetImplementationsList = ArrayList<Any>()
          zobowiazaniPierwotni.forEach {
            newBudgetImplementationsList.add(
              createObligatedPersonsElement(it)
            )
          }
          div.removeAll()
          components.readComponentsList(newBudgetImplementationsList, div)
          components.readComponentsList(createObligatedPersonsButtons(zobowiazaniPierwotni, div), div)
        }
      }
    )


  }

  fun createPartners(documentTreeNode: DocumentTreeNode, wspolnik: ArrayList<Wspolnik>): ArrayList<Any> {
    val finnId = "collapseFinn_" + Util.getRandomString()
    val partner = ArrayList<Any>()
    val obligatedPartner = ArrayList<Any>()

    wspolnik.forEach {
      obligatedPartner.add(createNewPartner(it))
    }

    val collapsedNPDiv = Div(className = "collapse") { id = finnId }

    val collapsePerson = components.createCollapseCheckbox(
      documentTreeNode,
      "Dane wspólników spółki nieposiadającej osobowości prawnej",
      finnId,
      obligatedPartner,
      collapsedNPDiv
    )
    partner.add(collapsePerson)
    components.changeElementChildrenStatus(
      obligatedPartner,
      (collapsePerson.field as CheckBox).value
    )
    components.readComponentsList(obligatedPartner, collapsedNPDiv)
    components.readComponentsList(createButtonGroup(documentTreeNode, wspolnik, collapsedNPDiv), collapsedNPDiv)
    partner.add(collapsedNPDiv)
    return partner
  }

  private fun createNewPartner(it: Wspolnik): ArrayList<Any> {
    val partner = ArrayList<Any>()
    val primaryObligatedMap = HashMap<String, Any?>()
    val naturalPartner =
      createNaturalPerson(
        it.osobaFizyczna,
        "Normal",
        "Osoba fizyczna"
      )
    val nonPhysicalPartner = createNonPhysicalPerson(
      it.osobaNiefizyczna,
      "Medium"
    )
    primaryObligatedMap["0"] = naturalPartner
    primaryObligatedMap["1"] = nonPhysicalPartner
    partner.add(
      components.createRadioGroup(
        it.partner,
        "Wspólnik",
        Tw1Data.optionsOfPrimalObligated,
        primaryObligatedMap
      )
    )
    partner.add(primaryObligatedMap)
    return partner
  }

  fun createSubject(namespaceURI: String?, podmiot: Podmiot): ArrayList<Any> {
    val subject = ArrayList<Any>()
    val TW1_5_10693 = "http://crd.gov.pl/wzor/2021/06/15/10693/"
    subject.add(
      components.createInput(
        podmiot.nazwa,
        "Nazwa organu"
      )
    )
    subject.add(
      address.createAddress(
        podmiot.adres,
        podmiot.adresData
      )
    )
    subject.add(
      components.createInput(
        podmiot.nrTelefonu,
        "Numer telefonu",
        requirements = FormReadingUtils.checkValidation("TNrTelefonu")
      )
    )
    subject.add(
      components.createInput(
        podmiot.email,
        "Adres e-mail",
        requirements = FormReadingUtils.checkValidation("TAdresEmail")
      )
    )
    subject.add(
      components.createInput(
        podmiot.skrytkaEPuap,
        "Adres skrzynki ePUAP",
        requirements =
        if(namespaceURI == TW1_5_10693)
          FormReadingUtils.checkValidation("TSkrytkaEPuap_10693")
        else
          FormReadingUtils.checkValidation("TSkrytkaEPuap_12139")
      )
    )
    return subject
  }

  fun createButtonGroup(
    documentTreeNode: DocumentTreeNode,
    parentElements: ArrayList<Wspolnik>,
    div: Div
  ): Div {
    return (
        Div(className = "p-2 d-flow-root") {
          if (parentElements.size > 1) {
            button(
              "Usuń",
              "far fa-minus-square",
              ButtonStyle.OUTLINEPRIMARY, className = "btn-sm"
            ).onClick {
              formReference.getData()

              formReference.form.remove(parentElements.get(parentElements.lastIndex).partner)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaFizyczna.adres)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaFizyczna.admId)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaFizyczna.imie)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaFizyczna.email)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaFizyczna.dataUrodzenia)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaFizyczna.nazwisko)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaFizyczna.nip1)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaFizyczna.nip2)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaFizyczna.nrTelefonu)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaFizyczna.odpowiedzialnosc)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaFizyczna.oswiadczenieWierzyciela)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaFizyczna.pesel1)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaFizyczna.pesel2)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaFizyczna.adresData.adresPolski.kodPocztowy)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaFizyczna.adresData.adresPolski.nrDomu)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaFizyczna.adresData.adresPolski.nrLokalu)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaFizyczna.adresData.adresPolski.ulica)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaFizyczna.adresData.adresPolski.gmina)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaFizyczna.adresData.adresPolski.miejscowosc)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaFizyczna.adresData.adresPolski.kodPocztowy)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaFizyczna.adresData.adresPolski.wojewodztwo)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaFizyczna.adresData.adresPolski.powiat)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaFizyczna.adresData.adresZagranicznyKodKraju)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaFizyczna.adresData.adresZagranicznyKodPocztowy)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaFizyczna.adresData.adresZagranicznyMiejscowosc)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaFizyczna.adresData.adresZagranicznyNrDomu)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaFizyczna.adresData.adresZagranicznyNrLokalu)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaFizyczna.adresData.adresZagranicznyUlica)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaNiefizyczna.admId)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaNiefizyczna.adres)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaNiefizyczna.email)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaNiefizyczna.nip1)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaNiefizyczna.nip2)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaNiefizyczna.nazwa)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaNiefizyczna.nrTelefonu)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaNiefizyczna.odpowiedzialnosc)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaNiefizyczna.oswiadczenie)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaNiefizyczna.regon1)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaNiefizyczna.regon2)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaNiefizyczna.wSpadku)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaNiefizyczna.adresData.adresPolski.kodPocztowy)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaNiefizyczna.adresData.adresPolski.nrDomu)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaNiefizyczna.adresData.adresPolski.nrLokalu)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaNiefizyczna.adresData.adresPolski.ulica)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaNiefizyczna.adresData.adresPolski.gmina)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaNiefizyczna.adresData.adresPolski.miejscowosc)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaNiefizyczna.adresData.adresPolski.kodPocztowy)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaNiefizyczna.adresData.adresPolski.wojewodztwo)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaNiefizyczna.adresData.adresPolski.powiat)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaNiefizyczna.adresData.adresZagranicznyKodKraju)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaNiefizyczna.adresData.adresZagranicznyKodPocztowy)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaNiefizyczna.adresData.adresZagranicznyMiejscowosc)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaNiefizyczna.adresData.adresZagranicznyNrDomu)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaNiefizyczna.adresData.adresZagranicznyNrLokalu)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).osobaNiefizyczna.adresData.adresZagranicznyUlica)
              parentElements.removeAt(parentElements.lastIndex)

              div.removeAll()

              val obligatedPartner = ArrayList<Any>()

              parentElements.forEach {
                obligatedPartner.add(createNewPartner(it))
              }

              val collapsePerson = div.id?.let { it1 ->
                components.createCollapseCheckbox(
                  documentTreeNode,
                  "Dane wspólników spółki nieposiadającej osobowości prawnej",
                  it1,
                  obligatedPartner,
                  div
                )
              }
              components.changeElementChildrenStatus(
                obligatedPartner,
                (collapsePerson?.field as CheckBox).value
              )

              components.readComponentsList(obligatedPartner, div)
              components.readComponentsList(createButtonGroup(documentTreeNode, parentElements, div), div)
            }
          }
          button(
            "Dodaj",
            "far fa-plus-square",
            ButtonStyle.OUTLINEPRIMARY, className = "btn-sm float-right"
          ).onClick {
            formReference.getData()
            parentElements.add(Wspolnik())
            div.removeAll()

            val obligatedPartner = ArrayList<Any>()

            parentElements.forEach {
              obligatedPartner.add(createNewPartner(it))
            }

            val collapsePerson = div.id?.let { it1 ->
              components.createCollapseCheckbox(
                documentTreeNode,
                "Dane wspólników spółki nieposiadającej osobowości prawnej",
                it1,
                obligatedPartner,
                div
              )
            }
            components.changeElementChildrenStatus(
              obligatedPartner,
              (collapsePerson?.field as CheckBox).value
            )

            components.readComponentsList(obligatedPartner, div)
            components.readComponentsList(createButtonGroup(documentTreeNode, parentElements, div), div)
          }
        }
        )
  }

  fun createAnAsset(assets: ArrayList<DocumentTreeNode>, label: String): ArrayList<FormElement> {
    val elements = ArrayList<FormElement>()
    assets.forEach {
      elements.add(components.createInput(it, label))
    }
    return elements
  }

  fun createButtonAssetGroup(parents: ArrayList<DocumentTreeNode>, div: Div, label: String): Div {
    return Div(className = "p-2 d-flow-root") {
      if (parents.size > 1) {
        button(
          "Usuń",
          "far fa-minus-square",
          ButtonStyle.OUTLINEPRIMARY, className = "btn-sm"
        ).onClick {
          formReference.getData()

          formReference.form.remove(parents.get(parents.lastIndex))
          parents.removeAt(parents.lastIndex)
          val partners = createAnAsset(parents, label)
          div.removeAll()

          components.readComponentsList(partners, div)
          components.readComponentsList(createButtonAssetGroup(parents, div, label), div)
        }
      }
      button(
        "Dodaj",
        "far fa-plus-square",
        ButtonStyle.OUTLINEPRIMARY, className = "btn-sm float-right"
      ).onClick {
        formReference.getData()
        parents.add((DocumentTreeNode()))

        val partners = createAnAsset(parents, label)

        div.removeAll()
        components.readComponentsList(partners, div)
        components.readComponentsList(createButtonAssetGroup(parents, div, label), div)
      }
    }
  }

  fun createReminderDeliveryData(assets: ArrayList<DocumentTreeNode>, label: String): ArrayList<FormElement> {
    val elements = ArrayList<FormElement>()
    assets.forEach {
      elements.add(components.createDateTime(it, label))
    }
    return elements
  }

  fun createButtonReminderDeliveryDataGroup(parents: ArrayList<DocumentTreeNode>, div: Div, label: String): Div {
    return Div(className = "p-2 d-flow-root") {
      if (parents.size > 1) {
        button(
          "Usuń",
          "far fa-minus-square",
          ButtonStyle.OUTLINEPRIMARY, className = "btn-sm"
        ).onClick {
          formReference.getData()

          formReference.form.remove(parents.get(parents.lastIndex))
          parents.removeAt(parents.lastIndex)
          val partners = createReminderDeliveryData(parents, label)
          div.removeAll()

          components.readComponentsList(partners, div)
          components.readComponentsList(createButtonReminderDeliveryDataGroup(parents, div, label), div)
        }
      }
      button(
        "Dodaj",
        "far fa-plus-square",
        ButtonStyle.OUTLINEPRIMARY, className = "btn-sm float-right"
      ).onClick {
        formReference.getData()
        parents.add((DocumentTreeNode()))

        val partners = createReminderDeliveryData(parents, label)

        div.removeAll()
        components.readComponentsList(partners, div)
        components.readComponentsList(createButtonReminderDeliveryDataGroup(parents, div, label), div)
      }
    }
  }
}